import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/24/outline";
import { Checkbox } from "components/ui/checkbox";
import { PrimaryButton } from "components/ui/primary-button";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useEffect, useState } from "react";

export type InlineFeedbackResponse = "positive" | "negative" | null;

export interface InlineFeedbackButtonsProps {
    submitted: InlineFeedbackResponse;
    submitting: boolean;
    positiveAriaLabel: string;
    negativeAriaLabel: string;
    onPositive: () => void;
    onNegative: () => void;
}

export const InlineFeedbackButtons: React.FC<InlineFeedbackButtonsProps> = ({
    submitted,
    submitting,
    positiveAriaLabel,
    negativeAriaLabel,
    onPositive,
    onNegative,
}) => (
    <div className="flex items-center gap-2">
        <button
            type="button"
            onClick={onPositive}
            disabled={submitting}
            className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                submitted === "positive"
                    ? "text-green-600 bg-green-50"
                    : "text-gray-400 hover:text-gray-600"
            }`}
            aria-label={positiveAriaLabel}>
            <HandThumbUpIcon className="w-5 h-5" />
        </button>
        <button
            type="button"
            onClick={onNegative}
            disabled={submitting}
            className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${
                submitted === "negative"
                    ? "text-gray-700 bg-gray-100"
                    : "text-gray-400 hover:text-gray-600"
            }`}
            aria-label={negativeAriaLabel}>
            <HandThumbDownIcon className="w-5 h-5" />
        </button>
    </div>
);

export interface InlineFeedbackFormProps {
    prompt: string;
    options: string[];
    submitting: boolean;
    onSubmit: (combinedText: string | null) => void;
}

export const InlineFeedbackForm: React.FC<InlineFeedbackFormProps> = ({
    prompt,
    options,
    submitting,
    onSubmit,
}) => {
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [feedbackText, setFeedbackText] = useState("");

    const handleToggleOption = useCallback((option: string) => {
        setSelectedOptions(prev =>
            prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option],
        );
    }, []);

    const handleSubmit = useCallback(() => {
        const trimmedText = feedbackText.trim();
        const parts = [
            ...selectedOptions,
            trimmedText.length > 0 ? trimmedText : null,
        ].filter((part): part is string => Boolean(part));
        const combined = parts.join("; ");
        onSubmit(combined.length > 0 ? combined : null);
    }, [feedbackText, selectedOptions, onSubmit]);

    return (
        <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">{prompt}</p>
            <div className="space-y-2">
                {options.map(option => (
                    <Checkbox
                        key={option}
                        label={option}
                        checked={selectedOptions.includes(option)}
                        onChange={() => handleToggleOption(option)}
                    />
                ))}
            </div>
            <TextArea
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                placeholder="Additional feedback (optional)"
                rows={3}
                textSize="sm"
            />
            <PrimaryButton onClick={handleSubmit} disabled={submitting}>
                {submitting ? "Sending..." : "Send Feedback"}
            </PrimaryButton>
        </div>
    );
};

export interface InlineFeedbackProps {
    negativeFeedbackPrompt: string;
    negativeFeedbackOptions: string[];
    submitted: InlineFeedbackResponse;
    submitting: boolean;
    positiveAriaLabel: string;
    negativeAriaLabel: string;
    onPositive: () => void;
    onNegativeIntent: () => void;
    onNegativeSubmit: (combinedText: string | null) => void;
}

export const InlineFeedback: React.FC<InlineFeedbackProps> = ({
    negativeFeedbackPrompt,
    negativeFeedbackOptions,
    submitted,
    submitting,
    positiveAriaLabel,
    negativeAriaLabel,
    onPositive,
    onNegativeIntent,
    onNegativeSubmit,
}) => {
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        if (submitted !== "negative") {
            setShowForm(false);
        }
    }, [submitted]);

    const handleThumbsDown = useCallback(() => {
        setShowForm(true);
        onNegativeIntent();
    }, [onNegativeIntent]);

    const handleSubmit = useCallback(
        (combinedText: string | null) => {
            onNegativeSubmit(combinedText);
            setShowForm(false);
        },
        [onNegativeSubmit],
    );

    return (
        <div className="space-y-2">
            <InlineFeedbackButtons
                submitted={submitted}
                submitting={submitting}
                positiveAriaLabel={positiveAriaLabel}
                negativeAriaLabel={negativeAriaLabel}
                onPositive={onPositive}
                onNegative={handleThumbsDown}
            />
            {showForm ? (
                <div className="mt-4 rounded-md bg-gray-50 p-4">
                    <InlineFeedbackForm
                        prompt={negativeFeedbackPrompt}
                        options={negativeFeedbackOptions}
                        submitting={submitting}
                        onSubmit={handleSubmit}
                    />
                </div>
            ) : null}
        </div>
    );
};
